// src/hooks/useAppNavigate.ts

/**
 * A custom navigation hook that provides type-safe routing throughout the application.
 * It centralizes all navigation logic and ensures consistency when navigating between routes.
 *
 * @returns An object with navigation methods for different routes
 *
 * @example
 * // Navigate to static routes
 * const { navigateToHome, navigateToAboutUs } = useAppNavigate()
 * navigateToHome()
 *
 * // Navigate to converter with parameters
 * const { navigateToConverter } = useAppNavigate()
 * navigateToConverter(lengthMeasure, meterUnit, feetUnit)
 * navigateTo(currentRoute, { replace: true })
 */

import { useNavigate, NavigateFunction } from 'react-router-dom'
import { ROUTES } from '../routes/routes'
import { Measure, Unit } from '../types'

interface NavigationOptions {
  replace?: boolean
  state?: any
}

interface AppNavigate {
  navigateToHome: (options?: NavigationOptions) => void
  navigateToContactUs: (options?: NavigationOptions) => void
  navigateToAboutUs: (options?: NavigationOptions) => void
  navigateToNotFound: (options?: NavigationOptions) => void
  navigateToConverter: (measure: Measure, fromUnit: Unit, toUnit: Unit, options?: NavigationOptions) => void
  navigateTo: NavigateFunction
}

export const useAppNavigate = (): AppNavigate => {
  const navigate = useNavigate()

  return {
    // Navigate to basic routes with options support
    navigateToHome: (options?: NavigationOptions) => navigate(ROUTES.HOME, options),
    navigateToContactUs: (options?: NavigationOptions) => navigate(ROUTES.CONTACT_US, options),
    navigateToAboutUs: (options?: NavigationOptions) => navigate(ROUTES.ABOUT_US, options),
    navigateToNotFound: (options?: NavigationOptions) => navigate(ROUTES.NOT_FOUND, options),

    // Navigate to converter with options support
    navigateToConverter: (
      measure: Measure,
      fromUnit: Unit,
      toUnit: Unit,
      options?: NavigationOptions
    ) => navigate(ROUTES.CONVERTER.create(measure, fromUnit, toUnit), options),

    // Direct access to the navigate function
    navigateTo: navigate
  }
}
